/**
 * Reports Page - Main Component
 * Feature-based folder structure with modular components
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Activity,
  AlertCircle,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  Eye,
  FileText,
  Loader2,
  Percent,
  Save,
  Target,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { csvImportService } from "../../api/services/csvImportService";
import { organizationsService } from "../../api/services/organizationsService";
import { reportService } from "../../api/services/reportService";
import { Toaster } from "../../components/ui/Toaster";
import { FEATURE_FLAGS } from "../../constants/appConstants";
import { useAuthStore } from "../../stores/authStore";
import { useSettingsStore } from "../../stores/settingsStore";
import {
  formatCompactCurrency,
  formatCompactNumber,
  formatPercentage,
  getChangeType,
} from "../../utils/formatters";
import ClientReportEditor from "./components/ClientReportEditor";
import CommentsSection from "./components/CommentsSection";
// Components
import DateRangePicker from "./components/DateRangePicker";
import DuplicateReportPortal from "./components/DuplicateReportPortal";
import MarketingCampaignsTable from "./components/MarketingCampaignsTable";
import MetricsByCampaignAdGroupAdTable from "./components/MetricsByCampaignAdGroupAdTable";
import MetricsByCreativeTable from "./components/MetricsByCreativeTable";
import type { MetricItem } from "./components/MetricsSummaryCard";
import MetricsSummaryCard from "./components/MetricsSummaryCard";
import TrafficSourceTable from "./components/TrafficSourceTable";
// Hooks & Data
import { useDateRange } from "./hooks/useDateRange";
import { useReportPolling } from "./hooks/useReportPolling";
import {
  Achievement,
  AIRecommendation,
  CampaignAdData,
  CampaignAdGroupByAd,
  CampaignAdRow,
  CampaignAdTotals,
  CampaignData,
  CampaignGroup,
  CampaignRow,
  CampaignTotals,
  CampaignWithAds,
  CreativeData,
  CreativeRow,
  DataAvailabilityStatus,
  Improvement,
  TrafficSourceData,
  TrafficSourceRow,
} from "./types";
import {
  comparisonOrUnavailable,
  metricHasValue,
  valueOrUnavailable,
} from "./utils/availability";
import { buildDraftPatchPayload } from "./utils/draftPatch";
import {
  buildReportCreateRequest,
  isMatchingCsvReport,
} from "./utils/reportCreation";

export default function Reports() {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // agencyId = tenant scope ID — required as X-T-Id header for all report API calls.
  // Platform-scope users have no scopeId in their JWT, so fall back to the agencyId
  // URL param set by OnboardingAssist when it navigated here.
  const agencyId = user?.scopeId || searchParams.get("agencyId") || undefined;

  // Client ID and Report ID state
  const [clientId, setClientId] = useState("");
  const [selectedOrgName, setSelectedOrgName] = useState("");
  const [selectedOrgAgencyId, setSelectedOrgAgencyId] = useState<
    string | undefined
  >(undefined);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);

  // resolvedAgencyId: use the JWT-scoped agency ID when available (tenant/agency users),
  // otherwise fall back to the agency ID derived from the selected org (platform users).
  // This single value is used for all API calls so polling and report fetch work consistently.
  const resolvedAgencyId = agencyId ?? selectedOrgAgencyId;

  // CSV-import mode state: set from URL params when arriving from OnboardingAssist
  const csvImportId = searchParams.get("csvImportId") ?? null;
  const csvClientId = searchParams.get("clientId") ?? null;
  const csvDateFrom = searchParams.get("dateFrom") ?? null;
  const csvDateTo = searchParams.get("dateTo") ?? null;
  const csvContextActive = Boolean(csvImportId && csvClientId);
  const activeClientId = csvContextActive ? csvClientId! : clientId;
  const [isMartBuilding, setIsMartBuilding] = useState(
    !!csvImportId && !searchParams.get("reportId"),
  );

  // true = just generated on this page; false = loaded from My Reports / recent list
  const [isNewlyGenerated, setIsNewlyGenerated] = useState(false);

  // Duplicate report dialog state (409 response)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateReportId, setDuplicateReportId] = useState("");
  const [duplicateReportStatus, setDuplicateReportStatus] = useState("");
  const [isLoadingExistingReport, setIsLoadingExistingReport] = useState(false);
  const [isForceRegenerating, setIsForceRegenerating] = useState(false);
  // Holds CSV params when a 409 is hit from the CSV poll, so force-regenerate
  // can replay the request with the correct CSV-specific params.
  const [pendingCsvParams, setPendingCsvParams] = useState<{
    clientId: string;
    dateFrom: string;
    dateTo: string;
    importId: string;
  } | null>(null);

  // Read reportId + optional initialStatus from URL (set by OnboardingAssist / MyReports)
  const initialStatusFromUrl = searchParams.get("initialStatus") as
    | "PENDING"
    | "PROCESSING"
    | "DRAFT"
    | "SUBMITTED"
    | "PUBLISHED"
    | "FAILED"
    | null;

  useEffect(() => {
    const reportIdFromUrl = searchParams.get("reportId");
    if (reportIdFromUrl) {
      setReportId(reportIdFromUrl);
      setIsNewlyGenerated(false);
    }
  }, [searchParams]);

  // CSV-import mode: poll mart build status, then call createReport once ready.
  // This runs only when arriving from OnboardingAssist with ?csvImportId=...
  useEffect(() => {
    if (
      !csvImportId ||
      !resolvedAgencyId ||
      !csvClientId ||
      !csvDateFrom ||
      !csvDateTo
    )
      return;
    if (reportId) {
      setIsMartBuilding(false);
      return;
    }

    let cancelled = false;
    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 90_000;
    const start = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        const fresh = await csvImportService.getById(
          csvImportId,
          resolvedAgencyId,
        );
        if (
          fresh.mart_build_status === "ready" ||
          fresh.mart_build_status === "failed" ||
          Date.now() - start >= POLL_TIMEOUT_MS
        ) {
          if (cancelled) return;
          // Mart is ready (or timed out) — create the report now
          const response = await reportService.createReport(
            buildReportCreateRequest({
              clientId: csvClientId,
              dateFrom: csvDateFrom,
              dateTo: csvDateTo,
              csvImportId,
            }),
            resolvedAgencyId,
          );
          const newReportId = response.responseData?.report_id;
          if (newReportId && !cancelled) {
            if (!isMatchingCsvReport(response.responseData, csvImportId)) {
              setIsMartBuilding(false);
              toast.error(
                t("reports.csvContextMismatch") ||
                  "CSV context was not preserved. Report generation was stopped.",
              );
              return;
            }
            setReportId(newReportId);
            setIsMartBuilding(false);
            // Fire-and-forget: mark import as REPORT_GENERATED
            void csvImportService
              .markReportGenerated(csvImportId, newReportId, resolvedAgencyId)
              .catch(() => {
                /* non-fatal */
              });
          }
        } else {
          setTimeout(() => {
            void poll();
          }, POLL_INTERVAL_MS);
        }
      } catch (err) {
        // On error: check for 409 DUPLICATE_REPORT and show the same dialog
        // that normal report generation uses — so the user can load the existing
        // report or force-override without navigating away.
        const axiosErr = err as AxiosError<{
          traceMessage?: string;
          responseData?: { existing_report_id?: string; status?: string };
        }>;
        if (
          axiosErr.response?.status === 409 &&
          axiosErr.response.data?.traceMessage === "DUPLICATE_REPORT"
        ) {
          const existingId =
            axiosErr.response.data.responseData?.existing_report_id ?? "";
          const existingStatus =
            axiosErr.response.data.responseData?.status ?? "";
          setDuplicateReportId(existingId);
          setDuplicateReportStatus(existingStatus);
          // Store CSV params so force-regenerate can replay with correct args
          if (csvImportId && csvClientId && csvDateFrom && csvDateTo) {
            setPendingCsvParams({
              clientId: csvClientId,
              dateFrom: csvDateFrom,
              dateTo: csvDateTo,
              importId: csvImportId,
            });
            // Do NOT call markReportGenerated here — wait for the user's choice.
            // handleLoadExistingReport links to existingId;
            // handleForceRegenerate links to the newly created report_id.
          }
          setShowDuplicateDialog(true);
        }
        setIsMartBuilding(false);
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvImportId, resolvedAgencyId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc: Go back to My Reports (when viewing a report)
      if (e.key === "Escape" && reportId) {
        navigate("/app/my-reports");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [reportId, navigate]);

  // Fetch organizations
  const { data: organizationsData, isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["organizations"],
    queryFn: organizationsService.getAll,
  });

  const organizations = useMemo(
    () => organizationsData || [],
    [organizationsData],
  );

  // Lock the organization to the import owner while CSV context is active.
  // Changing the reporting period is supported; changing the organization is not.
  useEffect(() => {
    if (!csvContextActive || !csvClientId || !organizations.length) return;
    const org = organizations.find((item) => item.id === csvClientId);
    if (!org) return;
    setClientId(org.id);
    setSelectedOrgName(org.name);
    if (org.agencyId) setSelectedOrgAgencyId(org.agencyId);
  }, [csvClientId, csvContextActive, organizations]);

  // Pre-select client from ?preClientId= (set by AgencyConsole reports tab)
  useEffect(() => {
    const preClientId = searchParams.get("preClientId");
    if (!preClientId || !organizations.length) return;
    if (clientId) return; // already set — don't override user's selection
    const org = organizations.find((o) => o.id === preClientId);
    if (org) {
      setClientId(org.id);
      setSelectedOrgName(org.name);
      if (org.agencyId) setSelectedOrgAgencyId(org.agencyId);
    }
  }, [searchParams, organizations, clientId]);

  // Date range management
  const {
    dateRange,
    setDateRange,
    showDatePicker,
    setShowDatePicker,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    compareToPrevious,
    setCompareToPrevious,
    datePickerRef,
  } = useDateRange({
    initialStartDate: csvDateFrom,
    initialEndDate: csvDateTo,
  });

  // Report state
  const [operatorNotes, setOperatorNotes] = useState("");
  const [standardComments, setStandardComments] = useState<string[]>([]);
  const [aiPoweredAnalysis, setAiPoweredAnalysis] = useState<string[]>([]);

  // Section visibility toggle - single toggle for both Comments and Insights
  const [showCommentsAndInsights, _setShowCommentsAndInsights] = useState(true);

  // AI Insights state with comments
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [improvements, setImprovements] = useState<Improvement[]>([]);
  const [recommendations, setRecommendations] = useState<AIRecommendation[]>(
    [],
  );
  const [baselineAchievements, setBaselineAchievements] = useState<
    Achievement[]
  >([]);
  const [baselineImprovements, setBaselineImprovements] = useState<
    Improvement[]
  >([]);
  const [baselineRecommendations, setBaselineRecommendations] = useState<
    AIRecommendation[]
  >([]);
  const [baselineOperatorNotes, setBaselineOperatorNotes] = useState("");

  // Track stable IDs deleted during this editing session.
  // Reset whenever fresh data arrives from the server (after save or refetch).
  const [deletedAchievementIds, setDeletedAchievementIds] = useState<string[]>(
    [],
  );
  const [deletedImprovementIds, setDeletedImprovementIds] = useState<string[]>(
    [],
  );
  const [deletedRecommendationIds, setDeletedRecommendationIds] = useState<
    string[]
  >([]);

  // Comment handlers
  const handleAchievementCommentChange = (index: number, comment: string) => {
    setAchievements((prev) =>
      prev.map((item, i) => (i === index ? { ...item, comment } : item)),
    );
  };

  const handleImprovementCommentChange = (index: number, comment: string) => {
    setImprovements((prev) =>
      prev.map((item, i) => (i === index ? { ...item, comment } : item)),
    );
  };

  const handleRecommendationCommentChange = (
    index: number,
    comment: string,
  ) => {
    setRecommendations((prev) =>
      prev.map((item, i) => (i === index ? { ...item, comment } : item)),
    );
  };

  const isPersistedInsightId = (id?: string): id is string =>
    typeof id === "string" && !id.startsWith("temp-");

  const handleAddAchievement = (newAchievement: {
    title: string;
    metric: string;
    current_value: string;
    comparison?: string;
    context?: string;
    summary?: string;
  }) => {
    setAchievements((prev) => [...prev, { ...newAchievement, comment: "" }]);
  };

  const handleAddImprovement = (newImprovement: {
    title: string;
    area: string;
    metric: string;
    severity: "High" | "Medium" | "Low";
  }) => {
    setImprovements((prev) => [...prev, { ...newImprovement, comment: "" }]);
  };

  const handleAddRecommendation = (newRecommendation: {
    title: string;
    description: string;
    expected_outcomes?: string[];
    impact: "high" | "medium" | "low";
  }) => {
    setRecommendations((prev) => [
      ...prev,
      {
        id: `temp-rec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: newRecommendation.title,
        description: newRecommendation.description,
        ...(newRecommendation.expected_outcomes !== undefined
          ? { expected_outcomes: newRecommendation.expected_outcomes }
          : {}),
        impact: newRecommendation.impact,
        ai_generated: false,
        modified_by_human: true,
        comment: "",
      },
    ]);
  };

  // Delete handlers — capture the item's stable server ID before removing from local state
  const handleDeleteAchievement = (index: number) => {
    setAchievements((prev) => {
      const item = prev[index];
      const persistedId = item?.id;
      if (isPersistedInsightId(persistedId)) {
        setDeletedAchievementIds((ids) => [...ids, persistedId]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeleteImprovement = (index: number) => {
    setImprovements((prev) => {
      const item = prev[index];
      const persistedId = item?.id;
      if (isPersistedInsightId(persistedId)) {
        setDeletedImprovementIds((ids) => [...ids, persistedId]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeleteRecommendation = (index: number) => {
    setRecommendations((prev) => {
      const item = prev[index];
      const persistedId = item?.id;
      if (isPersistedInsightId(persistedId)) {
        setDeletedRecommendationIds((ids) => [...ids, persistedId]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpdateAchievement = (index: number, item: Achievement) => {
    setAchievements((prev) => prev.map((a, i) => (i === index ? item : a)));
  };

  const handleUpdateImprovement = (index: number, item: Improvement) => {
    setImprovements((prev) => prev.map((imp, i) => (i === index ? item : imp)));
  };

  const handleUpdateRecommendation = (
    index: number,
    item: {
      title: string;
      description: string;
      expected_outcomes?: string[];
      impact: "high" | "medium" | "low";
    },
  ) => {
    setRecommendations((prev) =>
      prev.map((rec, i) => (i === index ? { ...rec, ...item } : rec)),
    );
  };

  const syncInsightsFromServer = useCallback((insights: unknown) => {
    const normalizedInsights =
      (insights as {
        performance_overview?: {
          achievements?: Achievement[];
          improvements?: Improvement[];
        };
        recommendations?: AIRecommendation[];
        comments_and_notes?: {
          ai_powered_analysis?: string[];
          standard_comments?: string[];
        };
      } | null) || null;

    const serverAchievements =
      normalizedInsights?.performance_overview?.achievements || [];
    const serverImprovements =
      normalizedInsights?.performance_overview?.improvements || [];
    const serverRecommendations = normalizedInsights?.recommendations || [];

    setAchievements(serverAchievements);
    setImprovements(serverImprovements);
    setRecommendations(serverRecommendations);
    setStandardComments(
      normalizedInsights?.comments_and_notes?.standard_comments ?? [],
    );
    setAiPoweredAnalysis(
      normalizedInsights?.comments_and_notes?.ai_powered_analysis ?? [],
    );

    setBaselineAchievements(serverAchievements);
    setBaselineImprovements(serverImprovements);
    setBaselineRecommendations(serverRecommendations);
    setBaselineOperatorNotes("");

    setDeletedAchievementIds([]);
    setDeletedImprovementIds([]);
    setDeletedRecommendationIds([]);
  }, []);

  // Helper function to calculate date range based on preset
  const getDateRange = () => {
    const today = new Date().toISOString().split("T")[0]!;

    if (dateRange === "custom") {
      return {
        date_from: customStartDate || today,
        date_to: customEndDate || today,
      };
    }

    if (dateRange === "today") {
      return {
        date_from: today,
        date_to: today,
      };
    }

    if (dateRange === "last7days") {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0]!;
      return {
        date_from: sevenDaysAgo,
        date_to: today,
      };
    }

    if (dateRange === "last30days") {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0]!;
      return {
        date_from: thirtyDaysAgo,
        date_to: today,
      };
    }

    // Default fallback
    return {
      date_from: today,
      date_to: today,
    };
  };

  // Create Report Mutation
  const createReportMutation = useMutation({
    mutationFn: (forceRegenerate?: boolean) => {
      const { date_from, date_to } = getDateRange();
      return reportService.createReport(
        buildReportCreateRequest({
          clientId: activeClientId,
          dateFrom: date_from,
          dateTo: date_to,
          ...(forceRegenerate !== undefined ? { forceRegenerate } : {}),
          csvImportId,
        }),
        resolvedAgencyId,
      );
    },
    onSuccess: (data) => {
      if (csvImportId && !isMatchingCsvReport(data.responseData, csvImportId)) {
        toast.error(
          t("reports.csvContextMismatch") ||
            "CSV context was not preserved. Report generation was stopped.",
        );
        return;
      }
      setShowDuplicateDialog(false);
      setReportId(data.responseData.report_id);
      setIsNewlyGenerated(true);
      toast.success(
        t("reports.reportGenerationStarted") || "Report generation started",
      );
      if (csvImportId && resolvedAgencyId) {
        void csvImportService
          .markReportGenerated(
            csvImportId,
            data.responseData.report_id,
            resolvedAgencyId,
          )
          .catch(() => {
            /* non-fatal */
          });
      }
    },
    onError: (
      error: AxiosError<{
        message: string;
        traceMessage?: string;
        responseData?: { existing_report_id?: string; status?: string };
      }>,
    ) => {
      if (
        error.response?.status === 409 &&
        error.response.data?.traceMessage === "DUPLICATE_REPORT"
      ) {
        const existingId =
          error.response.data.responseData?.existing_report_id ?? "";
        const existingStatus = error.response.data.responseData?.status ?? "";
        setDuplicateReportId(existingId);
        setDuplicateReportStatus(existingStatus);
        if (csvImportId) {
          const { date_from, date_to } = getDateRange();
          setPendingCsvParams({
            clientId: activeClientId,
            dateFrom: date_from,
            dateTo: date_to,
            importId: csvImportId,
          });
        }
        setShowDuplicateDialog(true);
        return;
      }
      toast.error(
        error.response?.data?.message ||
          t("reports.reportGenerationFailed") ||
          "Failed to generate report",
      );
    },
  });

  // Save Draft Mutation (PATCH /reports/{id}/draft)
  const saveDraftMutation = useMutation({
    mutationFn: (updates: Parameters<typeof reportService.editDraft>[1]) =>
      reportService.editDraft(reportId!, updates, resolvedAgencyId),
    onSuccess: (data) => {
      // Server response is authoritative: replace local state (including newly assigned IDs)
      syncInsightsFromServer(data?.responseData?.ai_insights || null);
      // Invalidate full report cache so draft_version reflects the increment
      void queryClient.invalidateQueries({ queryKey: ["report", reportId] });
      toast.success(t("reports.draftSaved") || "Draft saved successfully");
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ||
          t("reports.saveDraftFailed") ||
          "Failed to save draft",
      );
    },
  });

  // Submit Report Mutation (Draft → Published)
  const submitReportMutation = useMutation({
    mutationFn: () => reportService.submitReport(reportId!, resolvedAgencyId),
    onSuccess: () => {
      toast.success(
        t("reports.reportSubmitted") || "Report submitted for publishing",
      );
      void queryClient.invalidateQueries({ queryKey: ["report", reportId] });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ||
          t("reports.reportSubmitFailed") ||
          "Failed to submit report",
      );
    },
  });

  // Retry Failed Report Mutation
  const retryReportMutation = useMutation({
    mutationFn: () => reportService.retryReport(reportId!, resolvedAgencyId),
    onSuccess: () => {
      toast.success(
        t("reports.reportRetryStarted") || "Retrying report generation",
      );
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ||
          t("reports.reportRetryFailed") ||
          "Failed to retry report",
      );
    },
  });

  // Use intelligent polling hook for report status
  const queryClient = useQueryClient();
  const {
    status: pollingStatus,
    isPolling,
    errorMessage: pollingErrorMessage,
  } = useReportPolling({
    reportId,
    agencyId: resolvedAgencyId,
    ...(initialStatusFromUrl ? { initialStatus: initialStatusFromUrl } : {}),
    onDraft: () => {
      toast.success(t("reports.reportReady") || "Report is ready for editing!");
      // Invalidate so the full report query fires now that status is DRAFT
      void queryClient.invalidateQueries({ queryKey: ["report", reportId] });
    },
    onPublished: () => {
      toast.success(
        t("reports.reportPublished") || "Report has been published!",
      );
      // Refetch full report to pick up pdf_available: true
      void queryClient.invalidateQueries({ queryKey: ["report", reportId] });
    },
    onFailed: (_id, errorMessage) => {
      toast.error(
        errorMessage || t("reports.reportFailed") || "Report generation failed",
      );
    },
  });

  // Fetch full report data only when status is known to have content
  // Per spec: DRAFT (agency edits), SUBMITTED (read-only, PDF pending), PUBLISHED (final)
  // PENDING / PROCESSING have no metrics yet — do not call this endpoint prematurely
  const { data: reportData, isLoading: isLoadingReport } = useQuery({
    queryKey: ["report", reportId],
    queryFn: () => reportService.getReportById(reportId!, resolvedAgencyId),
    enabled:
      !!reportId &&
      (pollingStatus === "DRAFT" ||
        pollingStatus === "SUBMITTED" ||
        pollingStatus === "PUBLISHED"),
  });

  // Fetch recent reports (limit 3) - only when not viewing a specific report.
  // Include clientId in the key so switching orgs immediately re-fetches.
  const { data: recentReportsData } = useQuery({
    queryKey: ["reports", "recent", resolvedAgencyId, clientId],
    queryFn: () =>
      reportService.listReports(
        { limit: 3, ...(clientId ? { client_id: clientId } : {}) },
        resolvedAgencyId,
      ),
    // Require resolvedAgencyId so the X-T-Id header is always present; also skip when viewing a specific report
    enabled: !!resolvedAgencyId && !reportId,
  });

  const recentReports = recentReportsData?.responseData?.items || [];

  // Extract metrics from API response
  const metrics = reportData?.responseData?.metrics?.summary;
  const isCsvReport = reportData?.responseData?.data_source === "csv";
  const ga4Availability = metrics?.data_availability?.["ga4"];
  const ga4Status = isCsvReport
    ? "unsupported"
    : (ga4Availability?.status ??
      (metrics?.ga4_rows_present === false
        ? "not_collected"
        : metrics
          ? "available"
          : undefined));
  const showGa4Metrics =
    ga4Status === "available" ||
    ga4Status === "measured_zero" ||
    ga4Status === "partial";
  const dataStatusKey = (status: DataAvailabilityStatus) =>
    ({
      available: "reports.dataStatusAvailable",
      measured_zero: "reports.dataStatusMeasuredZero",
      not_collected: "reports.dataStatusNotCollected",
      unsupported: "reports.dataStatusUnsupported",
      sync_error: "reports.dataStatusSyncError",
      partial: "reports.dataStatusPartial",
    })[status];
  const dataStatusTone = (status: DataAvailabilityStatus) =>
    status === "available"
      ? darkMode
        ? "border-green-800 bg-green-950/30 text-green-300"
        : "border-green-200 bg-green-50 text-green-800"
      : status === "measured_zero" || status === "partial"
        ? darkMode
          ? "border-amber-800 bg-amber-950/30 text-amber-300"
          : "border-amber-200 bg-amber-50 text-amber-800"
        : status === "sync_error"
          ? darkMode
            ? "border-red-800 bg-red-950/30 text-red-300"
            : "border-red-200 bg-red-50 text-red-800"
          : darkMode
            ? "border-gray-700 bg-gray-800 text-gray-300"
            : "border-gray-200 bg-gray-50 text-gray-700";
  const sourceAvailability = [
    ...(ga4Status ? [{ key: "ga4", label: "GA4", status: ga4Status }] : []),
    ...(metrics?.data_availability?.["google_ads"]
      ? [
          {
            key: "google_ads",
            label: "Google Ads",
            status: metrics.data_availability["google_ads"].status,
          },
        ]
      : []),
    ...(metrics?.data_availability?.["meta_ads"]
      ? [
          {
            key: "meta_ads",
            label: "Meta Ads",
            status: metrics.data_availability["meta_ads"].status,
          },
        ]
      : []),
  ];
  const aiInsights = reportData?.responseData?.ai_insights;

  // Fee context: per-channel fee data from API  { google_ads: { fee_included, fee_percent }, ... }
  type FeeContextEntry = { fee_included: boolean; fee_percent: number };
  const feeContext: Record<string, FeeContextEntry> | undefined = reportData
    ?.responseData?.metrics?.fee_context as
    | Record<string, FeeContextEntry>
    | undefined;
  const anyFeeIncluded = Object.values(feeContext ?? {}).some(
    (c) => c.fee_included,
  );

  const trafficSourcesRaw: TrafficSourceRow[] = (reportData?.responseData
    ?.metrics?.traffic_sources || []) as TrafficSourceRow[];

  // Process traffic sources: separate TOTAL from individual sources
  const trafficSourcesData: TrafficSourceData = {
    sources: trafficSourcesRaw.filter(
      (row) => row.default_channel_group !== "TOTAL",
    ),
    total:
      trafficSourcesRaw.find((row) => row.default_channel_group === "TOTAL") ||
      null,
  };

  // Process creative metrics data
  const creativesRaw: CreativeRow[] = (reportData?.responseData?.metrics
    ?.by_creative || []) as CreativeRow[];
  const creativesData: CreativeData = {
    creatives: creativesRaw.filter((row) => row.creative_name !== "TOTAL"),
    total: creativesRaw.find((row) => row.creative_name === "TOTAL") || null,
  };

  // Process campaign data: group by campaign
  const campaignsRaw: CampaignRow[] = (reportData?.responseData?.metrics
    ?.by_campaign || []) as CampaignRow[];
  const campaignTotals: CampaignTotals = (reportData?.responseData?.metrics
    ?.campaign_totals || {
    avg_cvr: 0,
    total_clicks: 0,
    total_conversion_value: 0,
    total_conversions: 0,
    total_sessions: 0,
  }) as CampaignTotals;

  // Group ad groups by campaign
  const campaignMap = new Map<string, CampaignGroup>();
  campaignsRaw.forEach((adGroup) => {
    const key =
      adGroup.campaign_id || adGroup.campaign_name || "unknown_campaign";
    if (!campaignMap.has(key)) {
      campaignMap.set(key, {
        campaign_id: key,
        campaign_name: adGroup.campaign_name,
        platform: adGroup.platform,
        total_sessions: 0,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: null,
        cvr: 0,
        revenue_mom: null,
        revenue_yoy: null,
        ad_groups: [],
      });
    }
    const campaign = campaignMap.get(key)!;
    campaign.ad_groups.push(adGroup);
    campaign.total_sessions += adGroup.sessions;
    campaign.total_clicks += adGroup.clicks;
    campaign.total_conversions += adGroup.conversions;
    if (adGroup.revenue != null && adGroup.revenue > 0) {
      campaign.total_revenue = (campaign.total_revenue ?? 0) + adGroup.revenue;
    }
  });

  // Calculate campaign-level metrics
  campaignMap.forEach((campaign) => {
    // CVR = conversions / clicks
    campaign.cvr =
      campaign.total_clicks > 0
        ? campaign.total_conversions / campaign.total_clicks
        : 0;

    // Calculate weighted average for revenue_mom and revenue_yoy
    let totalRevenuePrevious = 0;
    let totalRevenueYoyPrevious = 0;
    campaign.ad_groups.forEach((adGroup) => {
      if (adGroup.revenue != null && adGroup.revenue_mom !== null) {
        totalRevenuePrevious += adGroup.revenue / (1 + adGroup.revenue_mom);
      }
      if (adGroup.revenue != null && adGroup.revenue_yoy !== null) {
        totalRevenueYoyPrevious += adGroup.revenue / (1 + adGroup.revenue_yoy);
      }
    });

    campaign.revenue_mom =
      campaign.total_revenue != null && totalRevenuePrevious > 0
        ? (campaign.total_revenue - totalRevenuePrevious) / totalRevenuePrevious
        : null;
    campaign.revenue_yoy =
      campaign.total_revenue != null && totalRevenueYoyPrevious > 0
        ? (campaign.total_revenue - totalRevenueYoyPrevious) /
          totalRevenueYoyPrevious
        : null;
  });

  const campaignData: CampaignData = {
    campaigns: Array.from(campaignMap.values()),
    totals: campaignTotals,
  };

  // Process campaign -> ad group -> ad data (new API field: by_campaign_ad)
  const campaignsAdRaw: CampaignAdRow[] = (reportData?.responseData?.metrics
    ?.by_campaign_ad || []) as CampaignAdRow[];

  const campaignWithAdsMap = new Map<string, CampaignWithAds>();
  const adGroupByCampaignMap = new Map<
    string,
    Map<string, CampaignAdGroupByAd>
  >();

  campaignsAdRaw.forEach((ad) => {
    const campaignKey =
      ad.campaign_id || ad.campaign_name || "unknown_campaign";
    const adGroupKey = ad.ad_group_id || ad.ad_group_name || "unknown_ad_group";

    if (!campaignWithAdsMap.has(campaignKey)) {
      campaignWithAdsMap.set(campaignKey, {
        campaign_id: campaignKey,
        campaign_name: ad.campaign_name,
        platform: ad.platform,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: null,
        cvr: 0,
        revenue_mom: null,
        revenue_yoy: null,
        ad_groups: [],
      });
    }

    if (!adGroupByCampaignMap.has(campaignKey)) {
      adGroupByCampaignMap.set(campaignKey, new Map());
    }

    const campaign = campaignWithAdsMap.get(campaignKey)!;
    campaign.total_clicks += ad.clicks;
    campaign.total_conversions += ad.conversions;
    if (ad.revenue != null && ad.revenue > 0) {
      campaign.total_revenue = (campaign.total_revenue ?? 0) + ad.revenue;
    }

    const adGroupMap = adGroupByCampaignMap.get(campaignKey)!;
    if (!adGroupMap.has(adGroupKey)) {
      adGroupMap.set(adGroupKey, {
        ad_group_id: adGroupKey,
        ad_group_name: ad.ad_group_name,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: null,
        cvr: 0,
        revenue_mom: null,
        revenue_yoy: null,
        ads: [],
      });
    }

    const adGroup = adGroupMap.get(adGroupKey)!;
    adGroup.total_clicks += ad.clicks;
    adGroup.total_conversions += ad.conversions;
    if (ad.revenue != null && ad.revenue > 0) {
      adGroup.total_revenue = (adGroup.total_revenue ?? 0) + ad.revenue;
    }
    adGroup.ads.push(ad);
  });

  // Calculate CVR + weighted MoM/YoY at ad group and campaign levels
  campaignWithAdsMap.forEach((campaign) => {
    const adGroupMap = adGroupByCampaignMap.get(campaign.campaign_id);
    if (!adGroupMap) return;

    const groups = Array.from(adGroupMap.values());

    groups.forEach((group) => {
      group.cvr =
        group.total_clicks > 0
          ? group.total_conversions / group.total_clicks
          : 0;

      let groupRevenuePrevious = 0;
      let groupRevenueYoyPrevious = 0;
      group.ads.forEach((ad) => {
        if (ad.revenue != null && ad.revenue_mom !== null) {
          groupRevenuePrevious += ad.revenue / (1 + ad.revenue_mom);
        }
        if (ad.revenue != null && ad.revenue_yoy !== null) {
          groupRevenueYoyPrevious += ad.revenue / (1 + ad.revenue_yoy);
        }
      });

      group.revenue_mom =
        group.total_revenue != null && groupRevenuePrevious > 0
          ? (group.total_revenue - groupRevenuePrevious) / groupRevenuePrevious
          : null;
      group.revenue_yoy =
        group.total_revenue != null && groupRevenueYoyPrevious > 0
          ? (group.total_revenue - groupRevenueYoyPrevious) /
            groupRevenueYoyPrevious
          : null;
    });

    campaign.ad_groups = groups;
    campaign.cvr =
      campaign.total_clicks > 0
        ? campaign.total_conversions / campaign.total_clicks
        : 0;

    let campaignRevenuePrevious = 0;
    let campaignRevenueYoyPrevious = 0;
    groups.forEach((group) => {
      group.ads.forEach((ad) => {
        if (ad.revenue != null && ad.revenue_mom !== null) {
          campaignRevenuePrevious += ad.revenue / (1 + ad.revenue_mom);
        }
        if (ad.revenue != null && ad.revenue_yoy !== null) {
          campaignRevenueYoyPrevious += ad.revenue / (1 + ad.revenue_yoy);
        }
      });
    });

    campaign.revenue_mom =
      campaign.total_revenue != null && campaignRevenuePrevious > 0
        ? (campaign.total_revenue - campaignRevenuePrevious) /
          campaignRevenuePrevious
        : null;
    campaign.revenue_yoy =
      campaign.total_revenue != null && campaignRevenueYoyPrevious > 0
        ? (campaign.total_revenue - campaignRevenueYoyPrevious) /
          campaignRevenueYoyPrevious
        : null;
  });

  const campaignAdTotals: CampaignAdTotals = campaignsAdRaw.reduce(
    (acc, row) => {
      acc.total_impressions =
        (acc.total_impressions ?? 0) + (row.impressions || 0);
      acc.total_clicks += row.clicks || 0;
      acc.total_conversions += row.conversions || 0;
      acc.total_cost = (acc.total_cost ?? 0) + (row.cost || 0);
      if (row.revenue != null && row.revenue > 0) {
        acc.total_revenue = (acc.total_revenue ?? 0) + row.revenue;
      }
      return acc;
    },
    {
      total_impressions: 0,
      total_clicks: 0,
      total_conversions: 0,
      total_cost: 0,
      total_revenue: null,
    } as CampaignAdTotals,
  );

  const campaignAdData: CampaignAdData = {
    campaigns: Array.from(campaignWithAdsMap.values()),
    totals: campaignAdTotals,
  };

  // Derive org name from the already-loaded organizations list using report's client_id (= org_id)
  const reportClientId = reportData?.responseData?.client_id;
  const orgName =
    organizations.find((org) => org.id === reportClientId)?.name ?? "";

  // Update AI Insights state when report data changes.
  // Also resets deleted-ID tracking so a stale deletion list never replays.
  // Always sync when reportData has loaded — this handles ai_insights: null correctly,
  // clearing stale insights from a previously viewed report.
  useEffect(() => {
    if (reportData !== undefined) {
      syncInsightsFromServer(aiInsights ?? null);
    }
  }, [reportData, aiInsights, syncInsightsFromServer]);

  // Acquisition metrics
  const acquisitionMetrics =
    metrics && showGa4Metrics
      ? [
          {
            icon: BarChart3,
            label: t("reports.sessions"),
            value: formatCompactNumber(metrics.acquisition.sessions),
            yoyChange: formatPercentage(metrics.acquisition.sessions_yoy, true),
            momChange: formatPercentage(metrics.acquisition.sessions_mom, true),
            yoyType: getChangeType(metrics.acquisition.sessions_yoy),
            momType: getChangeType(metrics.acquisition.sessions_mom),
          },
          {
            icon: Users,
            label: t("reports.newUsers"),
            value: formatCompactNumber(metrics.acquisition.new_users),
            yoyChange: formatPercentage(
              metrics.acquisition.new_users_yoy,
              true,
            ),
            momChange: formatPercentage(
              metrics.acquisition.new_users_mom,
              true,
            ),
            yoyType: getChangeType(metrics.acquisition.new_users_yoy),
            momType: getChangeType(metrics.acquisition.new_users_mom),
          },
          {
            icon: Percent,
            label: t("reports.newUserRate"),
            value: formatPercentage(metrics.acquisition.new_user_rate),
            yoyChange: formatPercentage(
              metrics.acquisition.new_user_rate_yoy,
              true,
            ),
            momChange: formatPercentage(
              metrics.acquisition.new_user_rate_mom,
              true,
            ),
            yoyType: getChangeType(metrics.acquisition.new_user_rate_yoy),
            momType: getChangeType(metrics.acquisition.new_user_rate_mom),
          },
        ]
      : [];

  // Engagement metrics
  const engagementMetrics =
    metrics && showGa4Metrics
      ? [
          {
            icon: Eye,
            label: t("reports.pageviews") + " (PV)",
            value: formatCompactNumber(metrics.engagement.pageviews),
            yoyChange: formatPercentage(metrics.engagement.pageviews_yoy, true),
            momChange: formatPercentage(metrics.engagement.pageviews_mom, true),
            yoyType: getChangeType(metrics.engagement.pageviews_yoy),
            momType: getChangeType(metrics.engagement.pageviews_mom),
          },
          {
            icon: FileText,
            label: t("reports.pagesPerSession"),
            value: metrics.engagement.pages_per_session.toFixed(1),
            yoyChange: formatPercentage(
              metrics.engagement.pages_per_session_yoy,
              true,
            ),
            momChange: formatPercentage(
              metrics.engagement.pages_per_session_mom,
              true,
            ),
            yoyType: getChangeType(metrics.engagement.pages_per_session_yoy),
            momType: getChangeType(metrics.engagement.pages_per_session_mom),
          },
          {
            icon: Activity,
            label: t("reports.engagementRate"),
            value: formatPercentage(metrics.engagement.engagement_rate),
            yoyChange: formatPercentage(
              metrics.engagement.engagement_rate_yoy,
              true,
            ),
            momChange: formatPercentage(
              metrics.engagement.engagement_rate_mom,
              true,
            ),
            yoyType: getChangeType(metrics.engagement.engagement_rate_yoy),
            momType: getChangeType(metrics.engagement.engagement_rate_mom),
          },
        ]
      : [];

  // Conversion metrics
  const conversionAvailability = metrics?.metric_availability?.["conversions"];
  const revenueAvailability = metrics?.metric_availability?.["revenue"];
  const conversionMetrics: MetricItem[] = metrics
    ? [
        {
          icon: Target,
          label: t("reports.conversions"),
          value: valueOrUnavailable(
            formatCompactNumber(metrics.conversion.conversions),
            conversionAvailability?.status,
          ),
          yoyChange: comparisonOrUnavailable(
            formatPercentage(metrics.conversion.conversions_yoy, true),
            conversionAvailability?.status,
          ),
          momChange: comparisonOrUnavailable(
            formatPercentage(metrics.conversion.conversions_mom, true),
            conversionAvailability?.status,
          ),
          yoyType: getChangeType(metrics.conversion.conversions_yoy),
          momType: getChangeType(metrics.conversion.conversions_mom),
          ...(conversionAvailability
            ? {
                availabilityStatus: conversionAvailability.status,
                availabilityLabel: t(
                  dataStatusKey(conversionAvailability.status),
                ),
              }
            : {}),
        },
        {
          icon: TrendingUp,
          label: t("reports.cvr"),
          value: valueOrUnavailable(
            formatPercentage(metrics.conversion.cvr),
            conversionAvailability?.status,
          ),
          yoyChange: comparisonOrUnavailable(
            formatPercentage(metrics.conversion.cvr_yoy, true),
            conversionAvailability?.status,
          ),
          momChange: comparisonOrUnavailable(
            formatPercentage(metrics.conversion.cvr_mom, true),
            conversionAvailability?.status,
          ),
          yoyType: getChangeType(metrics.conversion.cvr_yoy),
          momType: getChangeType(metrics.conversion.cvr_mom),
          ...(conversionAvailability
            ? {
                availabilityStatus: conversionAvailability.status,
                availabilityLabel: t(
                  dataStatusKey(conversionAvailability.status),
                ),
              }
            : {}),
        },
        {
          icon: DollarSign,
          label: t("reports.conversionValue"),
          value: metricHasValue(revenueAvailability?.status)
            ? formatCompactCurrency(metrics.conversion.revenue)
            : "N/A",
          yoyChange:
            metricHasValue(revenueAvailability?.status) &&
            metrics.conversion.revenue_yoy != null
              ? formatPercentage(metrics.conversion.revenue_yoy, true)
              : "—",
          momChange:
            metricHasValue(revenueAvailability?.status) &&
            metrics.conversion.revenue_mom != null
              ? formatPercentage(metrics.conversion.revenue_mom, true)
              : "—",
          yoyType: getChangeType(metrics.conversion.revenue_yoy ?? 0),
          momType: getChangeType(metrics.conversion.revenue_mom ?? 0),
          isCostMetric: true,
          ...(revenueAvailability
            ? {
                availabilityStatus: revenueAvailability.status,
                availabilityLabel: t(dataStatusKey(revenueAvailability.status)),
              }
            : {}),
        },
      ]
    : [];

  const handleGenerateReport = () => {
    if (!activeClientId.trim()) {
      toast.error(
        t("reports.pleaseEnterClientId") || "Please enter a client ID",
      );
      return;
    }
    createReportMutation.mutate(undefined);
  };

  const handleLoadExistingReport = async () => {
    // If this was triggered from a CSV import flow, link the import to the
    // existing report only after confirming that it belongs to this import.
    if (pendingCsvParams) {
      setIsLoadingExistingReport(true);
      try {
        const existing = await reportService.getReportById(
          duplicateReportId,
          resolvedAgencyId,
        );
        if (
          !isMatchingCsvReport(existing.responseData, pendingCsvParams.importId)
        ) {
          toast.error(
            t("reports.csvExistingContextMismatch") ||
              "The existing report does not belong to this CSV import. Generate a new Shadow Report instead.",
          );
          return;
        }

        setShowDuplicateDialog(false);
        setReportId(duplicateReportId);
        setIsNewlyGenerated(false);
        setPendingCsvParams(null);

        // History-link maintenance must not block an already verified report.
        void csvImportService
          .markReportGenerated(
            pendingCsvParams.importId,
            duplicateReportId,
            resolvedAgencyId ?? "",
          )
          .catch(() => {
            toast(
              t("reports.csvHistoryLinkUpdateFailed") ||
                "The report was loaded, but the import-history link could not be updated.",
              { icon: "⚠️" },
            );
          });
      } catch {
        toast.error(
          t("reports.csvExistingLoadFailed") ||
            "Could not verify the existing report. You can generate a new Shadow Report instead.",
        );
        return;
      } finally {
        setIsLoadingExistingReport(false);
      }
      return;
    }
    setShowDuplicateDialog(false);
    setReportId(duplicateReportId);
    setIsNewlyGenerated(false);
    setPendingCsvParams(null);
  };

  const handleForceRegenerate = async () => {
    if (pendingCsvParams) {
      // CSV import path: replay with the stored CSV-specific params + force_regenerate
      const csvParams = pendingCsvParams;
      setIsForceRegenerating(true);
      try {
        const response = await reportService.createReport(
          {
            client_id: csvParams.clientId,
            date_from: csvParams.dateFrom,
            date_to: csvParams.dateTo,
            report_type: "full",
            data_source: "csv",
            csv_import_id: csvParams.importId,
            force_regenerate: true,
          },
          resolvedAgencyId,
        );
        const newReportId = response.responseData?.report_id;
        if (!newReportId) return;
        if (!isMatchingCsvReport(response.responseData, csvParams.importId)) {
          toast.error(
            t("reports.csvContextMismatch") ||
              "CSV context was not preserved. Report generation was stopped.",
          );
          return;
        }
        setShowDuplicateDialog(false);
        setPendingCsvParams(null);
        setReportId(newReportId);
        setIsNewlyGenerated(true);
        toast.success(
          t("reports.reportGenerationStarted") || "Report generation started",
        );
        void csvImportService
          .markReportGenerated(
            csvParams.importId,
            newReportId,
            resolvedAgencyId ?? "",
          )
          .catch(() => {
            toast(
              t("reports.csvHistoryLinkUpdateFailed") ||
                "The report was generated, but the import-history link could not be updated.",
              { icon: "⚠️" },
            );
          });
      } catch (err) {
        const axiosErr = err as AxiosError<{ message: string }>;
        toast.error(
          axiosErr.response?.data?.message ||
            t("reports.reportGenerationFailed") ||
            "Failed to generate report",
        );
      } finally {
        setIsForceRegenerating(false);
      }
      return;
    }
    createReportMutation.mutate(true);
  };

  const handleSaveDraft = () => {
    if (!reportId) return;

    const payload = buildDraftPatchPayload({
      baselineAchievements,
      baselineImprovements,
      baselineRecommendations,
      currentAchievements: achievements,
      currentImprovements: improvements,
      currentRecommendations: recommendations,
      deletedAchievementIds,
      deletedImprovementIds,
      deletedRecommendationIds,
      baselineOperatorNotes,
      currentOperatorNotes: operatorNotes,
    });

    if (!payload) {
      toast(t("reports.noChangesToSave") || "No changes to save");
      return;
    }

    saveDraftMutation.mutate(payload);
  };

  const handleSubmitReport = () => {
    if (!reportId) return;
    submitReportMutation.mutate();
  };

  const handleRetryReport = () => {
    if (!reportId) return;
    retryReportMutation.mutate();
  };

  const handleDownloadPDF = async () => {
    if (!reportId) return;
    try {
      const response = await reportService.downloadPDF(
        reportId,
        resolvedAgencyId,
      );
      // Open signed URL in new tab for download
      window.open(response.responseData.download_url, "_blank");
      toast.success(t("reports.downloadStarted") || "Download started");
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message ||
          t("reports.downloadFailed") ||
          "Failed to download report",
      );
    }
  };

  // Get current report status (use pollingStatus or fallback to reportData)
  const currentStatus = pollingStatus || reportData?.responseData?.status;
  const isEditable = reportData?.responseData?.editable ?? false;
  const isPDFAvailable = reportData?.responseData?.pdf_available ?? false;

  const isProcessing =
    createReportMutation.isPending ||
    isLoadingReport ||
    isMartBuilding ||
    // Show skeleton while the initial status poll is in-flight (e.g. loaded from URL)
    (!!reportId && !!agencyId && isPolling) ||
    pollingStatus === "PENDING" ||
    pollingStatus === "PROCESSING";

  const hasReportData =
    !!reportData &&
    (currentStatus === "DRAFT" ||
      currentStatus === "PUBLISHED" ||
      currentStatus === "SUBMITTED");

  // Scroll the container to the top on first render so navigating here from
  // OnboardingAssist (or any deep-scrolled page) always starts at the top.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return (
    <div className="flex-1 p-4 md:p-8 overflow-y-auto">
      <div className="mb-6">
        <div
          className={`flex-col items-center justify-between mb-4 ${darkMode ? "text-gray-200" : "text-gray-800"}`}
        >
          {/* Breadcrumbs - Only show when viewing a report */}
          {reportId && (
            <nav
              className={`flex items-center gap-2 mb-4 text-sm ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
              aria-label="Breadcrumb"
            >
              <button
                onClick={() => navigate("/app/my-reports")}
                className={`hover:text-[var(--brand-primary)] transition-colors ${
                  darkMode
                    ? "hover:text-[var(--brand-primary)]"
                    : "hover:text-[var(--brand-primary)]"
                }`}
              >
                {t("reports.myReports") || "My Reports"}
              </button>
              <ChevronRight className="w-4 h-4" />
              <span
                className={darkMode ? "text-gray-200" : "text-gray-800"}
                aria-current="page"
              >
                {t("reports.reportDetails") || "Report Details"}
              </span>
            </nav>
          )}

          <h2 className="text-3xl font-bold mb-2">{t("reports.title")}</h2>
          <p className={darkMode ? "text-gray-400 mb-4" : "text-gray-600 mb-4"}>
            {t("reports.subtitle")}
          </p>

          {csvContextActive && !reportId && (
            <div
              className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
                darkMode
                  ? "border-blue-700 bg-blue-950/40 text-blue-200"
                  : "border-blue-200 bg-blue-50 text-blue-800"
              }`}
              role="status"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                {t("reports.csvContextActive") ||
                  "CSV Shadow Report mode is active. Changing the period keeps this imported CSV as the data source."}
              </span>
            </div>
          )}

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Organization Selector */}
            <div className="relative w-full sm:w-auto sm:min-w-[200px]">
              <button
                onClick={() => {
                  if (!csvContextActive) {
                    setShowOrgDropdown(!showOrgDropdown);
                  }
                }}
                disabled={csvContextActive}
                title={
                  csvContextActive
                    ? t("reports.csvOrganizationLocked") ||
                      "Organization is fixed by the CSV import"
                    : undefined
                }
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-sm transition-all ${
                  darkMode
                    ? "bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700"
                    : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50"
                } ${csvContextActive ? "cursor-not-allowed opacity-70" : ""}`}
              >
                <span className="truncate">
                  {isLoadingOrgs
                    ? t("reports.loadingOrganizations") || "Loading..."
                    : selectedOrgName ||
                      t("reports.selectOrganization") ||
                      "Select Organization"}
                </span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 transition-transform ${
                    showOrgDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showOrgDropdown && !isLoadingOrgs && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowOrgDropdown(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setShowOrgDropdown(false);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Close dropdown"
                  />
                  {/* Dropdown Menu */}
                  <div
                    className={`absolute left-0 right-0 top-full mt-2 max-h-64 overflow-y-auto rounded-xl shadow-xl border py-2 z-20 ${
                      darkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    {organizations.length === 0 ? (
                      <div
                        className={`px-4 py-2 text-sm ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {t("reports.noOrganizations") ||
                          "No organizations available"}
                      </div>
                    ) : (
                      organizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => {
                            setClientId(org.id);
                            setSelectedOrgName(org.name);
                            setSelectedOrgAgencyId(org.agencyId);
                            setShowOrgDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                            darkMode
                              ? "text-gray-300 hover:bg-gray-700"
                              : "text-gray-700 hover:bg-gray-50"
                          } ${
                            clientId === org.id
                              ? darkMode
                                ? "bg-gray-700"
                                : "bg-gray-50"
                              : ""
                          }`}
                        >
                          <div className="font-medium">{org.name}</div>
                          <div
                            className={`text-xs ${
                              darkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            {org.id}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <DateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
              showDatePicker={showDatePicker}
              setShowDatePicker={setShowDatePicker}
              customStartDate={customStartDate}
              setCustomStartDate={setCustomStartDate}
              customEndDate={customEndDate}
              setCustomEndDate={setCustomEndDate}
              compareToPrevious={compareToPrevious}
              setCompareToPrevious={setCompareToPrevious}
              datePickerRef={datePickerRef}
            />

            {/* Generate Report Button */}
            <button
              onClick={handleGenerateReport}
              disabled={isProcessing || !activeClientId.trim()}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-medium shadow-sm whitespace-nowrap ${
                isProcessing || !activeClientId.trim()
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-[var(--brand-primary)] text-white hover:opacity-90"
              }`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("reports.processing") || "Processing..."}
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4" />
                  {hasReportData
                    ? t("reports.generateNewReport") || "Generate New Report"
                    : t("reports.generateReport") || "Generate Report"}
                </>
              )}
            </button>

            {/* Retry Button (FAILED only) */}
            {currentStatus === "FAILED" && (
              <button
                onClick={handleRetryReport}
                disabled={retryReportMutation.isPending}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium shadow-sm bg-orange-500 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {retryReportMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4" />
                )}
                {t("reports.retry") || "Retry"}
              </button>
            )}

            {/* Download PDF Button (when PDF is available) */}
            {isPDFAvailable && (
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium shadow-sm bg-green-600 text-white hover:opacity-90 whitespace-nowrap"
              >
                <FileText className="w-4 h-4" />
                {t("reports.downloadPDF") || "Download PDF"}
              </button>
            )}

            {/* Section Visibility Toggle */}
            {/* <div
              className={`flex items-center gap-3 px-3 py-1.5 rounded-xl border ml-auto ${
                darkMode
                  ? "bg-gray-800/50 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            > */}
            {/* Comments and Insights Toggle */}
            {/* <label className="flex items-center gap-1.5 cursor-pointer">
                <span
                  className={`text-xs font-medium whitespace-nowrap ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {t("reports.commentsAndInsights") || "Comments and Insights"}
                </span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={showCommentsAndInsights}
                    onChange={(e) => setShowCommentsAndInsights(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div
                    className={`w-8 h-4 rounded-full transition-colors ${
                      darkMode
                        ? "bg-gray-700 peer-checked:bg-[var(--brand-primary)]"
                        : "bg-gray-300 peer-checked:bg-[var(--brand-primary)]"
                    }`}
                  />
                  <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
                </div>
              </label> */}
            {/* </div> */}

            {/* <button
              className={`p-2.5 border rounded-xl transition-all ${
                darkMode
                  ? "bg-gray-800 border-gray-700 hover:bg-gray-700"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Settings
                className={`w-5 h-5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              />
            </button>

            <button
              disabled={!hasReportData}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-sm font-medium shadow-sm ${
                hasReportData
                  ? "bg-[var(--brand-secondary)] text-white hover:bg-[var(--brand-secondary-hover)]"
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
              }`}
            >
              <Download className="w-4 h-4" />
              {t("reports.export")}
            </button> */}
          </div>
        </div>

        {/* Processing/Loading State - Skeleton */}
        {isProcessing && (
          <div className="space-y-8 animate-pulse">
            {/* Status Message */}
            <div
              className={`rounded-xl p-4 border text-center ${
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Loader2 className="w-5 h-5 animate-spin text-[var(--brand-primary)]" />
                <span
                  className={`text-sm font-medium ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  {t("reports.generatingReport") || "Generating Report..."}
                </span>
              </div>
              <p
                className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}
              >
                {isMartBuilding
                  ? t("reports.generatingPleaseWait") ||
                    "This may take a few moments. Please keep this page open."
                  : t("reports.processingMessage") ||
                    "This may take a few moments. You can explore My Reports and come back any time."}
              </p>
            </div>

            {/* Skeleton Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-2 lg:gap-4">
              {[1, 2, 3].map((index) => (
                <div
                  key={index}
                  className={`rounded-3xl p-6 border ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  {/* Card Title */}
                  <div
                    className={`h-5 w-40 rounded mb-6 ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    }`}
                  />

                  {/* Metric Items */}
                  <div className="space-y-4">
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        {/* Icon placeholder */}
                        <div
                          className={`w-10 h-10 rounded-lg ${
                            darkMode ? "bg-gray-700" : "bg-gray-200"
                          }`}
                        />
                        <div className="flex-1 space-y-2">
                          {/* Label */}
                          <div
                            className={`h-3 w-24 rounded ${
                              darkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          />
                          {/* Value */}
                          <div
                            className={`h-4 w-16 rounded ${
                              darkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          />
                        </div>
                        {/* Changes */}
                        <div className="space-y-2">
                          <div
                            className={`h-3 w-12 rounded ${
                              darkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          />
                          <div
                            className={`h-3 w-12 rounded ${
                              darkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Skeleton Table Placeholders */}
            {/* Traffic Sources Table Skeleton */}
            <div
              className={`rounded-3xl p-4 sm:p-6 border ${
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Table Title */}
              <div
                className={`h-5 w-40 rounded mb-4 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              />
              {/* Table Header */}
              <div
                className={`flex gap-4 mb-3 pb-3 border-b ${
                  darkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {[1, 2, 3, 4, 5, 6].map((col) => (
                  <div
                    key={col}
                    className={`h-3 flex-1 rounded ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
              {/* Table Rows */}
              {[1, 2, 3, 4, 5].map((row) => (
                <div key={row} className="flex gap-4 py-3">
                  {[1, 2, 3, 4, 5, 6].map((col) => (
                    <div
                      key={col}
                      className={`h-4 flex-1 rounded ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Creative Metrics Table Skeleton */}
            <div
              className={`rounded-3xl p-4 sm:p-6 border ${
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Table Title */}
              <div
                className={`h-5 w-48 rounded mb-4 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              />
              {/* Table Header */}
              <div
                className={`flex gap-4 mb-3 pb-3 border-b ${
                  darkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {[1, 2, 3, 4, 5].map((col) => (
                  <div
                    key={col}
                    className={`h-3 flex-1 rounded ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
              {/* Table Rows */}
              {[1, 2, 3].map((row) => (
                <div key={row} className="flex gap-4 py-3">
                  {[1, 2, 3, 4, 5].map((col) => (
                    <div
                      key={col}
                      className={`h-4 flex-1 rounded ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Marketing Campaigns Table Skeleton */}
            <div
              className={`rounded-3xl p-4 sm:p-6 border ${
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Table Title */}
              <div
                className={`h-5 w-56 rounded mb-4 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              />
              {/* Table Header */}
              <div
                className={`flex gap-4 mb-3 pb-3 border-b ${
                  darkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                  <div
                    key={col}
                    className={`h-3 flex-1 rounded ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
              {/* Table Rows */}
              {[1, 2, 3, 4].map((row) => (
                <div key={row} className="flex gap-4 py-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                    <div
                      key={col}
                      className={`h-4 flex-1 rounded ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
              ))}
              {/* Summary Cards Skeleton */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {[1, 2, 3, 4].map((card) => (
                  <div
                    key={card}
                    className={`rounded-2xl p-3 border ${
                      darkMode
                        ? "bg-gray-700/30 border-gray-700"
                        : "bg-gray-100 border-gray-200"
                    }`}
                  >
                    <div
                      className={`h-3 w-20 rounded mb-2 ${
                        darkMode ? "bg-gray-600" : "bg-gray-300"
                      }`}
                    />
                    <div
                      className={`h-6 w-16 rounded ${
                        darkMode ? "bg-gray-600" : "bg-gray-300"
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Failed State - Show Error Message */}
        {currentStatus === "FAILED" && (
          <div
            className={`rounded-xl p-6 border text-center ${
              darkMode
                ? "bg-red-900/20 border-red-700"
                : "bg-red-50 border-red-200"
            }`}
          >
            <AlertCircle
              className={`w-12 h-12 mx-auto mb-4 ${darkMode ? "text-red-400" : "text-red-600"}`}
            />
            <h3
              className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("reports.reportGenerationFailed") ||
                "Report Generation Failed"}
            </h3>
            <p
              className={`text-sm mb-4 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              {reportData?.responseData?.error_message ||
                pollingErrorMessage ||
                t("reports.failedMessage") ||
                "An error occurred while generating the report. Please try again."}
            </p>
            <button
              onClick={handleRetryReport}
              disabled={retryReportMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium shadow-sm bg-orange-500 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mx-auto"
            >
              {retryReportMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4" />
              )}
              {t("reports.retry") || "Retry"}
            </button>
          </div>
        )}

        {/* No Data State / Recent Reports */}
        {!reportId && !isProcessing && (
          <>
            {/* Recent Reports Section */}
            {recentReports.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3
                    className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {t("reports.recentReports") || "Recent Reports"}
                  </h3>
                  <button
                    onClick={() => navigate("/app/my-reports")}
                    className={`text-sm font-medium transition-colors hover:text-[var(--brand-primary)] ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {t("reports.viewAllReports") || "View All Reports"} →
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {recentReports.map((report) => (
                    <button
                      key={report.report_id}
                      onClick={() => {
                        setReportId(report.report_id);
                        navigate(
                          `/app/reports?reportId=${report.report_id}${agencyId ? `&agencyId=${agencyId}` : ""}`,
                        );
                        setIsNewlyGenerated(false);
                      }}
                      className={`text-left p-4 rounded-xl border transition-all hover:shadow-lg hover:scale-[1.02] ${
                        darkMode
                          ? "bg-gray-800 border-gray-700 hover:border-[var(--brand-primary)]"
                          : "bg-white border-gray-200 hover:border-[var(--brand-primary)]"
                      }`}
                    >
                      {/* Status Badge */}
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            report.status === "DRAFT"
                              ? darkMode
                                ? "bg-blue-500/20 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                              : report.status === "PENDING" ||
                                  report.status === "PROCESSING"
                                ? darkMode
                                  ? "bg-yellow-500/20 text-yellow-300"
                                  : "bg-yellow-100 text-yellow-700"
                                : report.status === "SUBMITTED"
                                  ? darkMode
                                    ? "bg-purple-500/20 text-purple-300"
                                    : "bg-purple-100 text-purple-700"
                                  : report.status === "PUBLISHED"
                                    ? darkMode
                                      ? "bg-green-500/20 text-green-300"
                                      : "bg-green-100 text-green-700"
                                    : report.status === "FAILED"
                                      ? darkMode
                                        ? "bg-red-500/20 text-red-300"
                                        : "bg-red-100 text-red-700"
                                      : ""
                          }`}
                        >
                          {t(
                            `reports.status.${report.status?.toLowerCase() ?? ""}`,
                          ) || report.status}
                        </span>
                        {report.pdf_available && (
                          <span
                            className={`px-2 py-1 rounded-lg text-xs font-medium ${
                              darkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            PDF
                          </span>
                        )}
                        {/* Shadow Report Badge */}
                        {report.data_source === "csv" && (
                          <span
                            className={`px-2 py-1 rounded-lg text-xs font-medium ${
                              darkMode
                                ? "bg-purple-500/20 text-purple-400"
                                : "bg-purple-50 text-purple-600"
                            }`}
                            title={
                              t("reports.badge.shadowTitle") ||
                              "Generated from imported CSV data"
                            }
                          >
                            {t("reports.badge.shadow") || "Shadow"}
                          </span>
                        )}
                      </div>

                      {/* Date Range */}
                      <div className="space-y-1">
                        <p
                          className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {t("reports.dateRange") || "Date Range"}
                        </p>
                        <p
                          className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-900"}`}
                        >
                          {new Date(report.date_from).toLocaleDateString()} -{" "}
                          {new Date(report.date_to).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Generated Date */}
                      <div className="flex items-center gap-1.5 mt-3 text-xs">
                        <Clock
                          className={`w-3.5 h-3.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                        />
                        <span
                          className={
                            darkMode ? "text-gray-500" : "text-gray-500"
                          }
                        >
                          {report.generated_at
                            ? new Date(report.generated_at).toLocaleDateString()
                            : ""}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No Reports Yet */}
            {recentReports.length === 0 && (
              <div
                className={`rounded-3xl p-8 border text-center ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[var(--brand-primary)]" />
                <h3
                  className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("reports.noReportYet") || "No Report Generated Yet"}
                </h3>
                <p
                  className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  {t("reports.noReportMessage") ||
                    'Enter a Client ID, select a date range, and click "Generate Report" to get started.'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Report Data - Only show when data is available */}
        {hasReportData && (
          <div className="space-y-8">
            {/* Report Info Banner: generated for + date range */}
            <div
              className={`flex flex-wrap items-center gap-2 px-5 py-3 rounded-2xl border ${
                darkMode
                  ? "bg-gray-800/60 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <FileText
                className={`w-4 h-4 flex-shrink-0 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <span
                className={`text-sm ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {isNewlyGenerated
                  ? t("reports.generatedFor") || "Generated report for"
                  : t("reports.viewingFor") || "Viewing report for"}
              </span>
              {reportClientId && (
                <>
                  <Users
                    className={`w-4 h-4 flex-shrink-0 ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  />
                  <span
                    className={`text-sm font-semibold ${
                      darkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {orgName || reportClientId}
                  </span>
                </>
              )}
              {reportClientId && (
                <span
                  className={`text-sm ${
                    darkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  ·
                </span>
              )}
              <Clock
                className={`w-4 h-4 flex-shrink-0 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <span
                className={`text-sm font-semibold ${
                  darkMode ? "text-gray-100" : "text-gray-900"
                }`}
              >
                {reportData?.responseData?.date_from
                  ? new Date(
                      reportData.responseData.date_from,
                    ).toLocaleDateString()
                  : ""}
                {" – "}
                {reportData?.responseData?.date_to
                  ? new Date(
                      reportData.responseData.date_to,
                    ).toLocaleDateString()
                  : ""}
              </span>
              {/* Status badge */}
              {pollingStatus && (
                <>
                  <span
                    className={`text-sm ${
                      darkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    ·
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-medium ${
                      pollingStatus === "DRAFT"
                        ? darkMode
                          ? "bg-blue-500/20 text-blue-300"
                          : "bg-blue-100 text-blue-700"
                        : pollingStatus === "PROCESSING" ||
                            pollingStatus === "PENDING"
                          ? darkMode
                            ? "bg-yellow-500/20 text-yellow-300"
                            : "bg-yellow-100 text-yellow-700"
                          : pollingStatus === "SUBMITTED"
                            ? darkMode
                              ? "bg-purple-500/20 text-purple-300"
                              : "bg-purple-100 text-purple-700"
                            : pollingStatus === "PUBLISHED"
                              ? darkMode
                                ? "bg-green-500/20 text-green-300"
                                : "bg-green-100 text-green-700"
                              : pollingStatus === "FAILED"
                                ? darkMode
                                  ? "bg-red-500/20 text-red-300"
                                  : "bg-red-100 text-red-700"
                                : ""
                    }`}
                  >
                    {(pollingStatus === "PENDING" ||
                      pollingStatus === "PROCESSING") && (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    )}
                    {t(`reports.status.${pollingStatus.toLowerCase()}`) ||
                      pollingStatus}
                  </span>
                </>
              )}
            </div>

            {/* Overall Summary */}
            <div className="space-y-6">
              {sourceAvailability.length > 0 && (
                <div className="grid gap-2 md:grid-cols-3">
                  {sourceAvailability.map((source) => (
                    <div
                      key={source.key}
                      role="status"
                      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm ${dataStatusTone(source.status)}`}
                    >
                      <span className="font-semibold">
                        {t("reports.dataStatusLabel", {
                          source: source.label,
                        })}
                      </span>
                      <span>{t(dataStatusKey(source.status))}</span>
                      {source.key === "ga4" &&
                        source.status === "unsupported" && (
                          <span>{t("reports.ga4MetricsNotIncluded")}</span>
                        )}
                      {source.key === "ga4" &&
                        source.status === "not_collected" && (
                          <span>{t("reports.ga4NoDataForPeriod")}</span>
                        )}
                    </div>
                  ))}
                </div>
              )}
              <div
                className={`grid grid-cols-1 gap-4 md:gap-2 lg:gap-4 ${showGa4Metrics ? "md:grid-cols-3" : "md:grid-cols-1"}`}
              >
                {/* Acquisition Metrics */}
                {showGa4Metrics && (
                  <MetricsSummaryCard
                    title={t("reports.acquisitionMetrics")}
                    titleIcon={UserPlus}
                    accentColor="primary"
                    metrics={acquisitionMetrics}
                  />
                )}

                {/* Engagement Metrics */}
                {showGa4Metrics && (
                  <MetricsSummaryCard
                    title={t("reports.engagementMetrics")}
                    titleIcon={Activity}
                    accentColor="secondary"
                    metrics={engagementMetrics}
                  />
                )}

                {/* Conversion Metrics */}
                <MetricsSummaryCard
                  title={t("reports.conversionMetrics")}
                  titleIcon={Target}
                  accentColor="primary"
                  metrics={conversionMetrics}
                  feeIncluded={anyFeeIncluded}
                  feeTooltipLabel={
                    t("reports.costIncludesFee") || "Cost includes agency fee"
                  }
                />
              </div>
            </div>

            {/* Traffic Source Details Table */}
            {trafficSourcesData.sources.length > 0 ? (
              <TrafficSourceTable
                data={trafficSourcesData}
                titleKey="reports.trafficSourceDetails"
              />
            ) : (
              <div
                className={`rounded-3xl p-6 border text-center ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <h3
                  className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("reports.trafficSources")}
                </h3>
                <p
                  className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  {t("reports.noDataAvailable") || "No data available"}
                </p>
              </div>
            )}

            {/* Metrics by Creative Table */}
            {creativesData.creatives.length > 0 ? (
              <MetricsByCreativeTable data={creativesData} />
            ) : (
              <div
                className={`rounded-3xl p-6 border text-center ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <h3
                  className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("reports.metricsByCreative")}
                </h3>
                <p
                  className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  {t("reports.noDataAvailable") || "No data available"}
                </p>
              </div>
            )}

            {/* Marketing Campaign / Ad Spend Table */}
            {FEATURE_FLAGS.SHOW_METRICS_BY_CAMPAIGN_AD_GROUP &&
              (campaignData.campaigns.length > 0 ? (
                <MarketingCampaignsTable
                  data={campaignData}
                  showSessions={!isCsvReport}
                  titleKey="reports.metricsByCampaignAdGroup"
                />
              ) : (
                <div
                  className={`rounded-3xl p-6 border text-center ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {t("reports.marketingCampaigns")}
                  </h3>
                  <p
                    className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.noDataAvailable") || "No data available"}
                  </p>
                </div>
              ))}

            {/* Metrics by Campaign / Ad Group / Ad Table */}
            {FEATURE_FLAGS.SHOW_METRICS_BY_CAMPAIGN_AD &&
              (campaignAdData.campaigns.length > 0 ? (
                <MetricsByCampaignAdGroupAdTable
                  data={campaignAdData}
                  titleKey="reports.metricsByCampaignAdGroupAd"
                />
              ) : (
                <div
                  className={`rounded-3xl p-6 border text-center ${
                    darkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {t("reports.metricsByCampaignAdGroupAd")}
                  </h3>
                  <p
                    className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.noDataAvailable") || "No data available"}
                  </p>
                </div>
              ))}

            {/* Conversion & Acquisition Details */}
            {/* <ConversionDetails /> */}

            {/* Site Performance */}
            {/* <SitePerformance /> */}

            {/* Comments & Operations Notes */}
            {showCommentsAndInsights && (
              <CommentsSection
                operatorNotes={operatorNotes}
                setOperatorNotes={setOperatorNotes}
                standardComments={standardComments}
                aiPoweredAnalysis={aiPoweredAnalysis}
              />
            )}

            {/* Client-Facing Report Editing */}
            {showCommentsAndInsights && (
              <ClientReportEditor
                achievements={achievements}
                improvements={improvements}
                recommendations={recommendations}
                channelCharacteristics={
                  aiInsights?.performance_overview?.channel_characteristics ??
                  undefined
                }
                feeContext={feeContext}
                onAchievementCommentChange={handleAchievementCommentChange}
                onImprovementCommentChange={handleImprovementCommentChange}
                onRecommendationCommentChange={
                  handleRecommendationCommentChange
                }
                onDeleteAchievement={handleDeleteAchievement}
                onDeleteImprovement={handleDeleteImprovement}
                onDeleteRecommendation={handleDeleteRecommendation}
                onAddAchievement={handleAddAchievement}
                onAddImprovement={handleAddImprovement}
                onAddRecommendation={handleAddRecommendation}
                onUpdateAchievement={handleUpdateAchievement}
                onUpdateImprovement={handleUpdateImprovement}
                onUpdateRecommendation={handleUpdateRecommendation}
                canEdit={currentStatus === "DRAFT" && isEditable}
                reportStatus={currentStatus ?? undefined}
                onSubmitReport={handleSubmitReport}
                isSubmitting={submitReportMutation.isPending}
                aiInsightsMissing={(() => {
                  const ai = aiInsights as
                    | { _unavailable?: boolean; _reason?: string }
                    | null
                    | undefined;
                  return (
                    hasReportData &&
                    !!ai?._unavailable &&
                    ai?._reason !== "no_data"
                  );
                })()}
                aiInsightsNoData={(() => {
                  const ai = aiInsights as
                    | { _unavailable?: boolean; _reason?: string }
                    | null
                    | undefined;
                  return (
                    hasReportData &&
                    !!ai?._unavailable &&
                    ai?._reason === "no_data"
                  );
                })()}
              />
            )}

            {/* Bottom Action Buttons (DRAFT only) */}
            {currentStatus === "DRAFT" && isEditable && (
              <div
                className={`flex items-center justify-end gap-3 pt-4 mt-2 border-t ${
                  darkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {/* Save Draft */}
                <button
                  onClick={handleSaveDraft}
                  disabled={
                    saveDraftMutation.isPending ||
                    submitReportMutation.isPending
                  }
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm border transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {saveDraftMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saveDraftMutation.isPending
                    ? t("reports.saving") || "Saving..."
                    : t("reports.saveDraft") || "Save Draft"}
                </button>

                {/* Generate Client Facing Report */}
                <button
                  onClick={handleSubmitReport}
                  disabled={
                    submitReportMutation.isPending ||
                    saveDraftMutation.isPending
                  }
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-secondary)] text-white hover:opacity-90 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitReportMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  {submitReportMutation.isPending
                    ? t("reports.submitting") || "Submitting..."
                    : t("reports.generateClientFacingReport") ||
                      "Generate Client Facing Report"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <Tooltip
        id="fee-kpi-tooltip"
        place="top"
        style={{
          backgroundColor: "#ffffff",
          color: "#111827",
          borderRadius: "0.5rem",
          padding: "0.5rem 0.75rem",
          fontSize: "0.875rem",
          zIndex: 9999,
          maxWidth: "300px",
          wordWrap: "break-word",
          boxShadow:
            "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
          border: "1px solid #e5e7eb",
        }}
      />
      <Toaster />
      <DuplicateReportPortal
        isOpen={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        onLoadExisting={handleLoadExistingReport}
        onForceRegenerate={handleForceRegenerate}
        existingReportId={duplicateReportId}
        existingReportStatus={duplicateReportStatus}
        isCsvContext={Boolean(pendingCsvParams)}
        isLoadingExisting={isLoadingExistingReport}
        isRegenerating={createReportMutation.isPending || isForceRegenerating}
      />
    </div>
  );
}
